'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { IconCheck, IconArrowRight } from '@tabler/icons-react';
import { StrapiImage } from '@/components/ui/strapi-image';
import { PillButton } from '@/components/pill-button';
import { jakarta } from '@/lib/fonts';
import { strapiImage } from '@/lib/strapi/strapiImage';
import { cn, formatNumber } from '@/lib/utils';
import { Product } from '@/types/types';

interface TrainingHeroProps {
  product: Product;
  locale?: string;
  isService?: boolean;
  isTraining?: boolean;
}

// Helper to format title with **bold text** if provided
const formatHeadline = (text: string) => {
  if (!text) return null;
  if (!text.includes('**')) {
    return text;
  }
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-extrabold text-[#c02029]">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

export const TrainingHero = ({
  product,
  locale = 'fr',
  isService = false,
  isTraining = false,
}: TrainingHeroProps) => {
  const imageUrl =
    product.images && product.images.length > 0
      ? strapiImage(product.images[0].url)
      : null;

  // Key highlights (top 3 perks)
  const topPerks = (product.perks || []).slice(0, 3);

  const ctaHref = isService
    ? `/${locale}/contact?service=${encodeURIComponent(product.name)}`
    : `/${locale}/contact?training=${encodeURIComponent(product.name)}`;

  const ctaLabel = isService
    ? (locale === 'fr' ? 'Demander un devis' : 'Request a quote')
    : (locale === 'fr' ? 'Parler à un formateur' : 'Talk to a trainer');

  return (
    <div className="relative rounded-3xl overflow-hidden bg-white dark:bg-neutral-900 border border-[#e5e2f5] dark:border-neutral-800 p-6 sm:p-10 md:p-14 lg:p-16 shadow-xl shadow-[#301754]/5">
      {/* Subtle background ambient lighting glow */}
      <div className="absolute top-1/2 -translate-y-1/2 right-0 w-[450px] h-[450px] rounded-full blur-[140px] opacity-[0.04] bg-[#c02029] pointer-events-none" />

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Left Column: Title, Description, Checkmarks, CTA */}
        <div className="lg:col-span-7 flex flex-col justify-center">
          {/* Main Title */}
          <h1
            className={cn(
              jakarta.className,
              'text-3xl sm:text-4xl md:text-5xl font-bold text-[#2e1452] dark:text-white tracking-tight leading-[1.15] mb-5'
            )}
          >
            {formatHeadline(product.name)}
          </h1>

          {/* Description */}
          {product.description && (
            <p className="text-base sm:text-lg text-[#5e5873] dark:text-neutral-300 leading-relaxed max-w-xl mb-6">
              {product.description}
            </p>
          )}

          {/* Highlights / Checkmarks */}
          {topPerks.length > 0 && (
            <ul className="flex flex-col gap-3 mb-8">
              {topPerks.map((perk, index) => {
                const perkText = perk.title || perk.text || '';
                return (
                  <li
                    key={perk.id || index}
                    className="flex items-center gap-3 text-[#3b2d56] dark:text-neutral-200 text-sm sm:text-base font-medium"
                  >
                    <span className="w-5 h-5 rounded-full bg-[#c02029]/10 text-[#c02029] flex items-center justify-center flex-shrink-0">
                      <IconCheck className="w-3.5 h-3.5 stroke-[2.75]" />
                    </span>
                    <span>{perkText}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Signature Pill Button with Discreet Red */}
          <div className="flex flex-wrap items-center gap-4 mt-2">
            <PillButton href={ctaHref} text={ctaLabel} />

            {product.show_price !== false && product.price > 0 && (
              <span className="bg-white dark:bg-neutral-800 border border-[#e5e2f5] dark:border-neutral-700 text-[#2e1452] dark:text-neutral-100 text-base font-bold px-5 py-2.5 rounded-full shadow-sm">
                {formatNumber(product.price)} €
              </span>
            )}
          </div>
        </div>

        {/* Right Column: Illustration Image Box */}
        <div className="lg:col-span-5 flex items-center justify-center py-4 sm:py-6">
          <div className="relative group p-2 sm:p-3 rounded-3xl bg-[#fbfafc] dark:bg-neutral-950 shadow-xl shadow-[#301754]/5 border border-[#e5e2f5] dark:border-neutral-800 w-full max-w-[480px]">
            {/* Subtle discreet red ambient glow behind image */}
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-[#c02029]/15 via-rose-500/10 to-transparent blur-xl opacity-30 group-hover:opacity-60 transition duration-500 pointer-events-none" />

            <div className="relative aspect-[16/11] sm:aspect-[4/3] w-full rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 flex items-center justify-center p-4">
              {imageUrl ? (
                <StrapiImage
                  src={imageUrl}
                  alt={product.name}
                  width={600}
                  height={450}
                  className="object-contain max-h-[300px] w-auto transition-transform duration-700 ease-out group-hover:scale-105"
                />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-rose-50 dark:bg-[#c02029]/15 border border-[#c02029]/30 flex items-center justify-center text-[#c02029] text-2xl font-bold">
                  {product.name.charAt(0)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingHero;
